/**
 * Ask Resida Help messages: the landlord's bubbles, the assistant's answer with its Sources and
 * Helpful / Not helpful rating, and the fixed-outcome variants (declined, off-topic, no answer, error,
 * rate limited) with their actions.
 *
 * Model text is untrusted (NFR-SECURITY-5): it renders as plain text with only **bold** and /help/<id>
 * links recognised, never as HTML. Citations come from server-resolved article data and render only when
 * they point at /help/…. The streamed answer goes through the same renderer, so it is formatted as it
 * arrives; a ** that has not closed yet stays plain text until its partner arrives.
 */

import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Info, Loader2, TriangleAlert } from 'lucide-react';
import type { HelpBotMessage, HelpCitation, HelpRating } from '@resida/api-types/help';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { HelpEyebrow } from './ArticleList';
import { articleFocusKey } from './focus-keys';
import { RatingControls } from './RatingControls';

/** Splits model text into plain runs, **bold** runs and /help/<id> paths (odd indices are the matches). */
const MODEL_TEXT_TOKEN = /(\*\*[^*\n]+\*\*|\/help\/[a-z0-9-]+)/g;
/** While an answer streams only **bold** is recognised: a /help/ path is cut mid-word until its last token lands. */
const STREAMING_TEXT_TOKEN = /(\*\*[^*\n]+\*\*)/g;
const HELP_PATH = /^\/help\/([a-z0-9-]+)$/;

const BUBBLE_CLASS = 'rounded-2xl rounded-bl-sm bg-muted px-3.5 py-3 text-sm text-foreground';

interface OpenArticle {
  /** In the panel a cited article opens in place; without it the links navigate to /help/<id>. */
  onOpenArticle?: (articleId: string) => void;
}

function HelpPathLink({
  path,
  articleId,
  onOpenArticle,
  className,
  children,
  'data-testid': testId,
}: OpenArticle & { path: string; articleId: string; className?: string; children: ReactNode; 'data-testid': string }) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!onOpenArticle || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onOpenArticle(articleId);
  };
  return (
    <Link to={path} onClick={handleClick} className={className} data-testid={testId} data-focus-key={articleFocusKey(articleId)}>
      {children}
    </Link>
  );
}

function ModelText({
  text,
  citations,
  onOpenArticle,
  streaming = false,
  children,
}: OpenArticle & { text: string; citations: HelpCitation[]; streaming?: boolean; children?: ReactNode }) {
  return (
    <p className="whitespace-pre-wrap break-words" data-testid="help-message-text">
      {text.split(streaming ? STREAMING_TEXT_TOKEN : MODEL_TEXT_TOKEN).map((part, index) => {
        if (index % 2 === 0) return part;
        if (part.startsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
        const articleId = HELP_PATH.exec(part)?.[1];
        if (!articleId) return part;
        const title = citations.find((citation) => citation.articleId === articleId)?.title;
        return (
          <HelpPathLink
            key={index}
            path={part}
            articleId={articleId}
            onOpenArticle={onOpenArticle}
            className="inline-flex min-h-[44px] items-center font-medium text-primary underline underline-offset-2"
            data-testid={`help-text-link-${articleId}`}
          >
            {title ?? part}
          </HelpPathLink>
        );
      })}
      {children}
    </p>
  );
}

function CitationList({
  citations,
  showHeading,
  onOpenArticle,
}: OpenArticle & { citations: HelpCitation[]; showHeading: boolean }) {
  const safe = citations.filter((citation) => HELP_PATH.test(citation.url));
  if (safe.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5" data-testid="help-citations">
      {showHeading && <HelpEyebrow>Sources</HelpEyebrow>}
      {safe.map((citation) => (
        <HelpPathLink
          key={citation.articleId}
          path={citation.url}
          articleId={citation.articleId}
          onOpenArticle={onOpenArticle}
          className="flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm font-semibold text-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`help-citation-${citation.articleId}`}
        >
          <FileText className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0 break-words">{citation.title}</span>
        </HelpPathLink>
      ))}
    </div>
  );
}

/** The Contact support label; while the conversation is being attached it says so. */
export function ContactSupportLabel({ busy }: { busy: boolean }) {
  if (!busy) return <>Contact support</>;
  return (
    <>
      <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
      Attaching conversation…
    </>
  );
}

interface ChatMessageProps extends OpenArticle {
  message: HelpBotMessage;
  /** Retry applies to the last message only. */
  isLast: boolean;
  onRetry: () => void;
  onSearchHelp: () => void;
  onContactSupport: () => void;
  /** The conversation is being attached for support: Contact support shows a busy state. */
  contactBusy: boolean;
  onRated: (messageId: string, rating: HelpRating, offerEscalation: boolean) => void;
}

export function ChatMessage({
  message,
  isLast,
  onOpenArticle,
  onRetry,
  onSearchHelp,
  onContactSupport,
  contactBusy,
  onRated,
}: ChatMessageProps) {
  if (message.role === 'user') {
    return (
      <div
        className="max-w-[86%] self-end whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-primary px-3.5 py-2.5 text-sm text-primary-foreground"
        data-testid="help-message-user"
      >
        {message.text}
      </div>
    );
  }

  const outcome = message.outcome ?? 'answered';

  if (outcome === 'error') {
    return (
      <div
        role="alert"
        data-testid="help-error"
        className="flex flex-wrap items-center gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-[13px]"
      >
        <TriangleAlert className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        <span className="min-w-0 flex-1 text-foreground">{message.text}</span>
        {isLast && (
          <Button type="button" variant="outline" onClick={onRetry} data-testid="help-retry">
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (outcome === 'rate-limited') {
    return (
      <div
        role="status"
        data-testid="help-rate-limited-message"
        className="flex gap-2.5 rounded-lg border border-warning/25 bg-warning/10 p-3 text-[13px]"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
        <span className="text-foreground">{message.text}</span>
      </div>
    );
  }

  const offersEscalation = message.offerEscalation && outcome !== 'no-answer';

  return (
    <div className="flex max-w-[94%] flex-col gap-2.5 self-start" data-testid={`help-${outcome}`}>
      <div className={BUBBLE_CLASS}>
        <ModelText text={message.text} citations={message.citations} onOpenArticle={onOpenArticle} />
      </div>

      {(outcome === 'answered' || outcome === 'action-request') && (
        <CitationList citations={message.citations} showHeading={outcome === 'answered'} onOpenArticle={onOpenArticle} />
      )}

      {outcome === 'no-answer' && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={onSearchHelp} data-testid="help-no-answer-search">
            Search help
          </Button>
          <Button
            type="button"
            onClick={onContactSupport}
            disabled={contactBusy}
            aria-busy={contactBusy}
            data-testid="help-no-answer-contact"
          >
            <ContactSupportLabel busy={contactBusy} />
          </Button>
        </div>
      )}

      {outcome === 'answered' && <RatingControls message={message} onRated={onRated} />}

      {offersEscalation && (
        <Button
          type="button"
          variant="ghost"
          className="self-start text-primary"
          onClick={onContactSupport}
          disabled={contactBusy}
          aria-busy={contactBusy}
          data-testid={`help-escalate-${message.id}`}
        >
          <ContactSupportLabel busy={contactBusy} />
        </Button>
      )}
    </div>
  );
}

/** Three pulsing dots between sending and the first streamed token. */
export function TypingIndicator() {
  return (
    <div className={cn(BUBBLE_CLASS, 'self-start')} role="status" aria-label="Resida Help is typing" data-testid="help-typing">
      <span className="flex h-5 items-center gap-1" aria-hidden="true">
        {[0, 150, 300].map((delay) => (
          <i
            key={delay}
            className="block h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground motion-reduce:animate-none"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </span>
    </div>
  );
}

/** The answer as it streams in, formatted like the final message, with a caret at the end. */
export function StreamingMessage({ text }: { text: string }) {
  return (
    <div className="flex max-w-[94%] flex-col self-start" data-testid="help-streaming">
      <div className={BUBBLE_CLASS}>
        <ModelText text={text} citations={[]} streaming>
          <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-primary align-[-2px] motion-reduce:animate-none" aria-hidden="true" />
        </ModelText>
      </div>
    </div>
  );
}
